/**
 * SectionDraftDialog — modal del flujo "Generar sección con IA"
 * (Sprint AI-1.d.b).
 *
 * 4 estados internos según el progreso:
 *
 *   - 'estimate'  → carga estimate; muestra preview + botón Generar
 *   - 'streaming' → consume SSE y muestra steps en vivo
 *   - 'done'      → draft + citas + métricas; CTA Reemplazar contenido
 *   - 'error'     → error legible + opción Reintentar / Cerrar
 *
 * Usa los hooks `useDraftEstimate` y `useStreamDraft`. El padre solo
 * controla `open`, `onClose` y el callback `onAccept` que recibe el
 * markdown final cuando el user hace "Reemplazar contenido".
 *
 * **Tokens visibles, costos invisibles** (Sprint AI-1.d.b): toda
 * referencia a USD se omite intencionalmente. Solo tokens y latency.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Circle,
  Loader2,
  Sparkles,
  XCircle,
} from 'lucide-react'

import type {
  AgentMetrics,
  Citation,
  ContextWarning,
  DraftEstimate,
  LoadedContextSection,
  ResolvedTokenInfo,
  WaitingPayload,
} from '../../api/agents'
import type { GroundingReport } from '../../api/citations'
import { ApiError } from '../../api/client'
import { apiErrorCode, useAiVersionLockOn } from '../../hooks/useAiVersionLock'
import { useDraftEstimate } from '../../hooks/useDraftEstimate'
import { type StepState, useStreamDraft } from '../../hooks/useStreamDraft'
import { formatLanguage, formatProfileLabel } from '../../lib/aiLabels'
import { formatDuration } from '../../lib/formatDuration'
import { AiErrorNotice, AiWaitingNotice } from './AiStreamNotices'
import { CitationList } from './CitationList'
import { DraftPreview } from './DraftPreview'
import { GroundingFindingsSection } from './GroundingFindings'

/** Una sección referenciable desde el modal, lista para ser insertada
 * como token `{{section_id.value}}` en `user_instructions`. */
export interface AvailableTokenSection {
  section_id: string
  /** Título legible localizado (i18n del template). */
  title: string
  /** Camino jerárquico de títulos hasta el target (sin incluirlo).
   * Ej. ['Casos de uso', 'Caso de uso']. Vacío para secciones root. */
  path: string[]
  /** True si la sección es repetible — el modal lo señala porque el
   * token devolverá un array de instancias. */
  is_repeatable: boolean
}

/** Patrón de token usado para validar el textarea inline. Mismo regex
 * que el backend (`token_resolver._TOKEN_RE`). */
const TOKEN_REGEX = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\.([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g

interface SectionDraftDialogProps {
  open: boolean
  onClose: () => void
  artifactId: string
  sectionId: string
  sectionTitle: string
  /** Tipo de sección del template (rich_text, bullet_list, ...). El
   * agente usa este valor para adaptar el formato de output. Sin él,
   * las listas vienen como markdown libre con preámbulos mezclados. */
  sectionType: string
  /** Idioma del proyecto — solo para display informativo. */
  projectLanguage: string
  /** Contenido ACTUAL de la sección. Se usa para:
   *   1. Decidir si mostrar el checkbox "tomar como referencia"
   *      (aparece solo si hay contenido no-vacío).
   *   2. Pasarlo al backend si el user marca el checkbox. */
  currentValue?: unknown
  /** Lista de secciones del template que el user puede referenciar como
   * tokens `{{section_id.value}}`. Excluye la sección activa (sin
   * sentido auto-referenciarse). El padre la construye aplanando el
   * árbol del template + título localizado. */
  availableTokenSections?: AvailableTokenSection[]
  /** Sprint AI-1.f: path desde root al caller incluyendo índices de
   * instancias activas de repetibles. Vacío si la sección está en root.
   * Ejemplo (Diagrama del CU2/FA4):
   *   [{section_id: "casos_de_uso", index: 1},
   *    {section_id: "flujos_alternos", index: 3},
   *    {section_id: "diagrama"}]
   * Se manda al backend para activar casos 2/3 del resolver. */
  currentPath?: { section_id: string; index?: number }[]
  /** Doc 07 — version target. Cuando el artifact tiene canon activo y
   * el user está editando v(N+1) draft, este id apunta a esa draft. Sin
   * este id el backend cae sobre `current_version_id` (caso v1 inicial). */
  versionId?: string | null
  /** Callback que recibe el draft final cuando el user click
   * "Reemplazar contenido". Para tipos text es markdown; para listas
   * es un JSON array string que el padre parsea.
   *
   * `draftRunId` (OT-0049) — la corrida guardada en el servidor. El padre
   * guarda con ella las citas (con su evidencia) y el juicio por oración
   * de la sección (`applySectionDraftRunCitations`); nunca manda las
   * citas. */
  onAccept: (draftPayload: string, draftRunId: string) => void
}

export function SectionDraftDialog(props: SectionDraftDialogProps) {
  // El contenido se monta al abrir: cada apertura arranca con el estado
  // inicial, y cerrar lo desmonta (lo que corta el stream en curso).
  if (!props.open) return null
  return <SectionDraftContent {...props} />
}

function SectionDraftContent({
  onClose,
  artifactId,
  sectionId,
  sectionTitle,
  sectionType,
  projectLanguage,
  currentValue,
  availableTokenSections = [],
  currentPath = [],
  versionId,
  onAccept,
}: SectionDraftDialogProps) {
  const { t } = useTranslation('ai')

  const estimateQuery = useDraftEstimate({
    artifactId,
    sectionId,
    sectionType,
    versionId: versionId ?? null,
  })

  const stream = useStreamDraft()

  // OT-0064 — la versión dejó de ser editable con el diálogo abierto: la
  // estimación o el stream responden con un bloqueo por estado y se aplica
  // al instante.
  useAiVersionLockOn(
    artifactId,
    versionId,
    (stream.phase === 'error' ? stream.error?.code : undefined) ??
      apiErrorCode(estimateQuery.error),
  )

  // ─── Modo 2.1 — instrucciones extra del user ─────────────────────
  const [userInstructions, setUserInstructions] = useState('')
  // Checkbox "tomar como referencia el contenido actual". Solo
  // significativo cuando `currentValue` no está vacío. Default OFF
  // por seguridad — el user no quiere que la IA mezcle accidentalmente.
  const [useCurrentAsReference, setUseCurrentAsReference] = useState(false)
  const hasCurrentContent = isContentNonEmpty(currentValue)

  // Esc / backdrop close — bloqueamos cierre durante streaming para
  // evitar que el user pierda el progreso por accidente.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && stream.phase !== 'streaming') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [onClose, stream.phase])

  const handleStart = () => {
    stream.start({
      artifactId,
      sectionId,
      sectionType,
      userInstructions: userInstructions.trim() || null,
      currentContent:
        useCurrentAsReference && hasCurrentContent ? currentValue : null,
      currentPath,
      versionId: versionId ?? null,
    })
  }

  const handleReplace = () => {
    if (stream.result) {
      onAccept(stream.result.draft_text, stream.result.draft_run_id)
      onClose()
    }
  }

  const dialogTitle = (() => {
    if (stream.phase === 'done') {
      return t('dialog.title_done', { section: sectionTitle })
    }
    if (stream.phase === 'streaming') {
      return t('dialog.title_streaming', { section: sectionTitle })
    }
    if (stream.phase === 'error') {
      return t('dialog.title_error')
    }
    return t('dialog.title_estimate')
  })()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={dialogTitle}
      onClick={(e) => {
        if (e.target === e.currentTarget && stream.phase !== 'streaming') {
          onClose()
        }
      }}
    >
      <div className="mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col rounded-md border border-line bg-surface shadow-lg">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand" aria-hidden="true" />
            <h2 className="font-display text-lg text-ink">{dialogTitle}</h2>
          </div>
        </header>

        {/* Body — scroll si crece */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {stream.phase === 'idle' && (
            <EstimateBody
              estimateLoading={estimateQuery.isLoading}
              estimateError={estimateQuery.error as Error | null}
              estimate={estimateQuery.data ?? null}
              sectionTitle={sectionTitle}
              projectLanguage={projectLanguage}
              userInstructions={userInstructions}
              onUserInstructionsChange={setUserInstructions}
              hasCurrentContent={hasCurrentContent}
              useCurrentAsReference={useCurrentAsReference}
              onToggleCurrentAsReference={setUseCurrentAsReference}
              availableTokenSections={availableTokenSections.filter(
                (s) => s.section_id !== sectionId,
              )}
            />
          )}
          {stream.phase === 'streaming' && (
            <StreamingBody
              steps={stream.steps}
              tokensResolved={stream.tokensResolved}
              tokenWarnings={stream.tokenWarnings}
              contextWarnings={stream.contextWarnings}
              contextSectionsLoaded={stream.contextSectionsLoaded}
              waiting={stream.waiting}
            />
          )}
          {stream.phase === 'done' && stream.result && (
            <DoneBody
              draftText={stream.result.draft_text}
              sectionType={sectionType}
              citations={stream.result.citations}
              grounding={stream.result.grounding}
              metrics={stream.result.metrics}
              warnings={stream.warnings}
              tokensResolved={stream.tokensResolved}
              tokenWarnings={stream.tokenWarnings}
              contextWarnings={stream.contextWarnings}
              contextSectionsLoaded={stream.contextSectionsLoaded}
            />
          )}
          {stream.phase === 'error' && stream.error && (
            <AiErrorNotice
              code={stream.error.code}
              message={stream.error.message}
            />
          )}
        </div>

        {/* Footer — botones según estado */}
        <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-3">
          {stream.phase === 'idle' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleStart}
                disabled={estimateQuery.isLoading}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90 disabled:opacity-50"
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {t('dialog.actions.generate')}
              </button>
            </>
          )}
          {stream.phase === 'streaming' && (
            <button
              type="button"
              onClick={() => stream.abort()}
              className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
            >
              {t('dialog.actions.abort')}
            </button>
          )}
          {stream.phase === 'done' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleStart}
                className="inline-flex items-center gap-2 rounded-md border border-brand/40 px-3 py-1.5 text-sm text-brand hover:bg-brand/10"
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                {t('dialog.actions.regenerate')}
              </button>
              <button
                type="button"
                onClick={handleReplace}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90"
              >
                {t('dialog.actions.replace_content')}
              </button>
            </>
          )}
          {stream.phase === 'error' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-surface-muted"
              >
                {t('dialog.actions.close')}
              </button>
              <button
                type="button"
                onClick={handleStart}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand/90"
              >
                {t('dialog.actions.regenerate')}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  )
}

// ─── Sub-componentes por estado ───────────────────────────────────────────
function EstimateBody({
  estimateLoading,
  estimateError,
  estimate,
  sectionTitle,
  projectLanguage,
  userInstructions,
  onUserInstructionsChange,
  hasCurrentContent,
  useCurrentAsReference,
  onToggleCurrentAsReference,
  availableTokenSections,
}: {
  estimateLoading: boolean
  estimateError: Error | null
  estimate: DraftEstimate | null
  sectionTitle: string
  projectLanguage: string
  userInstructions: string
  onUserInstructionsChange: (next: string) => void
  hasCurrentContent: boolean
  useCurrentAsReference: boolean
  onToggleCurrentAsReference: (next: boolean) => void
  availableTokenSections: AvailableTokenSection[]
}) {
  const { t } = useTranslation('ai')
  const { t: tCommon } = useTranslation('common')
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Set de section_ids válidos — para validación inline en el textarea.
  const validSectionIds = useMemo(
    () => new Set(availableTokenSections.map((s) => s.section_id)),
    [availableTokenSections],
  )

  // Tokens detectados en el textarea + cuáles son inválidos.
  const detectedTokens = useMemo(
    () => detectTokens(userInstructions, validSectionIds),
    [userInstructions, validSectionIds],
  )

  // Insertar token en la posición del cursor.
  const insertToken = (sectionId: string) => {
    const ta = textareaRef.current
    const token = `{{${sectionId}.value}}`
    if (!ta) {
      onUserInstructionsChange(`${userInstructions}${token}`)
      return
    }
    const start = ta.selectionStart ?? userInstructions.length
    const end = ta.selectionEnd ?? userInstructions.length
    const next =
      userInstructions.slice(0, start) + token + userInstructions.slice(end)
    onUserInstructionsChange(next)
    // Restaurar foco y mover el cursor al final del token insertado.
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + token.length
      ta.setSelectionRange(pos, pos)
    })
  }

  if (estimateLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-subtle">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{t('common:loading') ?? 'Loading...'}</span>
      </div>
    )
  }

  if (estimateError instanceof ApiError) {
    return <ApiErrorView code={estimateError.code} />
  }

  return (
    <div className="space-y-3 text-sm">
      <Field label={t('dialog.section_label')} value={sectionTitle} />
      <Field
        label={t('dialog.language_label')}
        value={formatLanguage(projectLanguage, tCommon)}
      />
      {estimate && (
        <>
          <Field
            label={t('dialog.profile_label')}
            value={formatProfileLabel(estimate.profile_name)}
          />
          <Field
            label={t('dialog.estimated_tokens_label')}
            value={t('dialog.estimated_tokens_value', {
              value: formatTokens(estimate.estimated_total_tokens),
            })}
          />
          {Object.keys(estimate.models_used).length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wider text-ink-subtle">
                {t('dialog.models_label')}
              </p>
              <ul className="mt-1 space-y-0.5 font-mono text-xs">
                {Object.entries(estimate.models_used).map(([role, model]) => (
                  <li key={role}>
                    <span className="text-ink-muted">{role}:</span>{' '}
                    <span className="text-ink">{model}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {estimate.notes.length > 0 && (
            <ul className="rounded-md border border-review/40 bg-review-soft/40 p-2 text-xs text-ink-muted">
              {estimate.notes.map((n) => (
                <li key={n}>· {n}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {/* ─── Modo 2.1: Instrucciones extra del user ──────────────── */}
      <div className="space-y-1.5 pt-2">
        <div className="flex items-center justify-between gap-2">
          <label
            htmlFor="ai-user-instructions"
            className="text-xs uppercase tracking-wider text-ink-subtle"
          >
            {t('dialog.user_instructions_label')}
          </label>
          {availableTokenSections.length > 0 && (
            <TokenPicker
              availableTokenSections={availableTokenSections}
              onSelect={insertToken}
            />
          )}
        </div>
        <textarea
          id="ai-user-instructions"
          ref={textareaRef}
          value={userInstructions}
          onChange={(e) => onUserInstructionsChange(e.target.value)}
          placeholder={t('dialog.user_instructions_placeholder')}
          rows={3}
          maxLength={2000}
          className="w-full resize-y rounded-md border border-line bg-canvas px-3 py-2 text-sm font-body leading-relaxed focus:border-brand focus:outline-none"
        />
        <p className="text-[11px] text-ink-subtle">
          {t('dialog.user_instructions_hint')}
        </p>
        {detectedTokens.invalid.length > 0 && (
          <div className="rounded-md border border-critical/40 bg-critical-soft/30 px-2 py-1.5 text-[11px] text-ink">
            <p className="font-medium text-critical">
              {t('dialog.tokens_invalid_label')}
            </p>
            <ul className="mt-0.5 font-mono text-[10px] text-ink-muted">
              {detectedTokens.invalid.map((tok) => (
                <li key={tok}>· {tok}</li>
              ))}
            </ul>
          </div>
        )}
        {detectedTokens.valid.length > 0 &&
          detectedTokens.invalid.length === 0 && (
            <p className="text-[11px] text-ink-subtle">
              {t('dialog.tokens_valid_count', {
                count: detectedTokens.valid.length,
              })}
            </p>
          )}
      </div>

      {/* ─── Checkbox "tomar como referencia" ─────────────────────────
          Solo aparece si la sección YA tiene contenido. Default OFF. */}
      {hasCurrentContent && (
        <label className="flex items-start gap-2 rounded-md border border-line/60 bg-surface-muted/40 px-3 py-2 text-xs text-ink cursor-pointer">
          <input
            type="checkbox"
            checked={useCurrentAsReference}
            onChange={(e) => onToggleCurrentAsReference(e.target.checked)}
            className="mt-0.5 h-3.5 w-3.5 cursor-pointer"
          />
          <div>
            <p className="font-medium text-ink">
              {t('dialog.use_current_as_reference_label')}
            </p>
            <p className="text-[11px] text-ink-subtle leading-snug">
              {t('dialog.use_current_as_reference_hint')}
            </p>
          </div>
        </label>
      )}

      <p className="text-xs text-ink-subtle">{t('dialog.warning_replace')}</p>
    </div>
  )
}

// ─── TokenPicker ─────────────────────────────────────────────────────────
function TokenPicker({
  availableTokenSections,
  onSelect,
}: {
  availableTokenSections: AvailableTokenSection[]
  onSelect: (sectionId: string) => void
}) {
  const { t } = useTranslation('ai')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const containerRef = useRef<HTMLDivElement | null>(null)

  // Cerrar al click fuera.
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [open])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return availableTokenSections
    return availableTokenSections.filter((s) => {
      return (
        s.section_id.toLowerCase().includes(q) ||
        s.title.toLowerCase().includes(q) ||
        s.path.some((p) => p.toLowerCase().includes(q))
      )
    })
  }, [availableTokenSections, query])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11px] text-ink-muted hover:border-brand/40 hover:bg-brand/5 hover:text-ink"
      >
        {t('dialog.tokens_picker_button')}
        <ChevronDown className="h-3 w-3" aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-80 max-h-72 overflow-hidden rounded-md border border-line bg-surface shadow-lg flex flex-col">
          <div className="border-b border-line px-2 py-1.5">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('dialog.tokens_picker_search_placeholder')}
              autoFocus
              className="w-full rounded border border-line bg-canvas px-2 py-1 text-xs focus:border-brand focus:outline-none"
            />
          </div>
          <ul className="overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-[11px] text-ink-subtle">
                {t('dialog.tokens_picker_empty')}
              </li>
            )}
            {filtered.map((s) => (
              <li key={s.section_id}>
                <button
                  type="button"
                  onClick={() => {
                    onSelect(s.section_id)
                    setOpen(false)
                    setQuery('')
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-surface-muted"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-ink truncate">
                      {s.title}
                    </span>
                    {s.is_repeatable && (
                      <span className="rounded bg-brand/10 px-1 text-[9px] uppercase tracking-wider text-brand">
                        {t('dialog.tokens_picker_repeatable_badge')}
                      </span>
                    )}
                  </div>
                  {s.path.length > 0 && (
                    <p className="text-[10px] text-ink-subtle truncate">
                      {s.path.join(' › ')}
                    </p>
                  )}
                  <p className="font-mono text-[10px] text-ink-muted truncate">
                    {`{{${s.section_id}.value}}`}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ─── Tokens panel (compartido entre streaming + done) ─────────────────────
function TokensResolvedPanel({
  tokensResolved,
  tokenWarnings,
  contextWarnings,
}: {
  tokensResolved: ResolvedTokenInfo[]
  tokenWarnings: string[]
  contextWarnings: ContextWarning[]
}) {
  const { t } = useTranslation('ai')
  if (
    tokensResolved.length === 0 &&
    tokenWarnings.length === 0 &&
    contextWarnings.length === 0
  )
    return null
  return (
    <div className="rounded-md border border-line bg-surface-muted/40 px-3 py-2 text-[11px] text-ink">
      <p className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {t('dialog.tokens_resolved_label')}
      </p>
      {tokensResolved.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tokensResolved.map((tk) => (
            <li
              key={tk.raw}
              className="flex items-center justify-between gap-2"
            >
              <span className="font-mono text-[10px] text-ink-muted truncate">
                {tk.raw}
              </span>
              <span className="text-[10px] text-ink-subtle whitespace-nowrap">
                {tk.is_array
                  ? t('dialog.tokens_resolved_array', {
                      chars: tk.char_count,
                    })
                  : t('dialog.tokens_resolved_scalar', {
                      chars: tk.char_count,
                    })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {tokenWarnings.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {tokenWarnings.map((w) => (
            <li key={w} className="flex items-start gap-1 text-review">
              <AlertTriangle
                className="mt-0.5 h-3 w-3 shrink-0"
                aria-hidden="true"
              />
              <span className="text-[10px] leading-snug">{w}</span>
            </li>
          ))}
        </ul>
      )}
      {contextWarnings.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {contextWarnings.map((w, idx) => {
            // ADR-0002 Fase 10.f UX fix — localizar el code estable.
            // Si no hay traducción para un code desconocido, fallback al
            // `detail` (free-text del resolver intra) o al code crudo.
            const i18nKey = `dialog.context_warnings.${w.code}`
            const localized = t(i18nKey, { defaultValue: '' }) as string
            const message =
              localized || w.detail || w.code
            // Etiqueta legible: prefiere "Template · Sección" del
            // snapshot ya localizado; fallback al ref técnico.
            let displayLabel: string | null = null
            if (w.source_template_name && w.source_section_title) {
              displayLabel = `${w.source_template_name} · ${w.source_section_title}`
            } else if (w.source_template_name) {
              displayLabel = w.source_template_name
            } else if (w.ref) {
              displayLabel = w.ref
            }
            return (
              <li
                key={`${w.code}-${w.ref ?? ''}-${idx}`}
                className="flex items-start gap-1 text-review"
              >
                <AlertTriangle
                  className="mt-0.5 h-3 w-3 shrink-0"
                  aria-hidden="true"
                />
                <span className="text-[10px] leading-snug">
                  {message}
                  {displayLabel ? (
                    <span className="text-ink-subtle">
                      {' · '}
                      <span
                        className={
                          w.source_template_name
                            ? ''
                            : 'font-mono'
                        }
                      >
                        {displayLabel}
                      </span>
                    </span>
                  ) : null}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ─── Context loaded panel (Sub-fase 2.1++) ────────────────────────────────
function ContextLoadedPanel({
  contextSectionsLoaded,
}: {
  contextSectionsLoaded: LoadedContextSection[]
}) {
  const { t } = useTranslation('ai')
  if (contextSectionsLoaded.length === 0) return null
  return (
    <div className="rounded-md border border-line bg-surface-muted/40 px-3 py-2 text-[11px] text-ink">
      <p className="text-[10px] uppercase tracking-wider text-ink-subtle">
        {t('dialog.context_loaded_label')}
      </p>
      <ul className="mt-1 space-y-1.5">
        {contextSectionsLoaded.map((s) => (
          <li key={s.section_id} className="space-y-0.5">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-ink">{s.title}</span>
              <span className="whitespace-nowrap text-[10px] text-ink-subtle">
                {s.is_array
                  ? t('dialog.tokens_resolved_array', { chars: s.char_count })
                  : t('dialog.tokens_resolved_scalar', { chars: s.char_count })}
              </span>
            </div>
            {s.rendered_preview ? (
              <details className="ml-2 group">
                <summary className="text-[10px] text-ink-subtle hover:text-brand cursor-pointer select-none">
                  {t('dialog.context_loaded_show_content')}
                </summary>
                <pre className="mt-1 max-h-48 overflow-y-auto rounded border border-line bg-canvas px-2 py-1.5 text-[10px] font-mono whitespace-pre-wrap text-ink-muted">
                  {s.rendered_preview}
                </pre>
              </details>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

function StreamingBody({
  steps,
  tokensResolved,
  tokenWarnings,
  contextWarnings,
  contextSectionsLoaded,
  waiting,
}: {
  steps: StepState[]
  tokensResolved: ResolvedTokenInfo[]
  tokenWarnings: string[]
  contextWarnings: ContextWarning[]
  contextSectionsLoaded: LoadedContextSection[]
  waiting: WaitingPayload | null
}) {
  const { t } = useTranslation('ai')
  return (
    <div className="space-y-3">
      <ContextLoadedPanel contextSectionsLoaded={contextSectionsLoaded} />
      <TokensResolvedPanel
        tokensResolved={tokensResolved}
        tokenWarnings={tokenWarnings}
        contextWarnings={contextWarnings}
      />
      <ol className="space-y-2">
      {steps.map((step) => (
        <li
          key={step.name}
          className={`flex items-start gap-3 rounded-md border px-3 py-2 ${
            step.status === 'running'
              ? 'border-brand/30 bg-brand/5'
              : 'border-line'
          }`}
        >
          <StepIcon status={step.status} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium text-sm text-ink">
                {t(`steps.${step.name}`, {
                  defaultValue: step.name,
                })}
              </span>
              {step.modelId && (
                <span className="font-mono text-[10px] text-ink-subtle">
                  {step.modelId}
                </span>
              )}
            </div>
            {step.status === 'complete' && step.name !== 'retrieve' && (
              <p className="mt-0.5 text-xs text-ink-subtle">
                {t('steps.tokens_format', {
                  input: formatTokens(step.inputTokens),
                  output: formatTokens(step.outputTokens),
                })}
                {step.latencyMs > 0 && (
                  <>
                    {' · '}
                    {formatDuration(step.latencyMs, t)}
                  </>
                )}
              </p>
            )}
            {step.retrieve && (
              <p className="mt-0.5 text-xs text-ink-subtle">
                {step.retrieve.chunksCount === 0
                  ? t('steps.retrieve_summary_zero')
                  : t('steps.retrieve_summary', {
                      chunks: step.retrieve.chunksCount,
                      sources: step.retrieve.sourcesCount,
                    })}
              </p>
            )}
            {step.critique && (
              <>
                <p className="mt-0.5 text-xs text-ink-subtle">
                  {step.critique.passes
                    ? t('steps.critique_pass')
                    : t('steps.critique_fail', {
                        issues: step.critique.issuesCount,
                      })}
                </p>
                {step.critique.issues.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-[11px] text-ink-muted">
                    {step.critique.issues.map((it) => (
                      <li
                        key={it}
                        className="line-clamp-2 leading-snug"
                      >
                        · {it}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </li>
      ))}
      </ol>
      <AiWaitingNotice waiting={waiting} />
    </div>
  )
}

function DoneBody({
  draftText,
  sectionType,
  citations,
  grounding,
  metrics,
  warnings,
  tokensResolved,
  tokenWarnings,
  contextWarnings,
  contextSectionsLoaded,
}: {
  draftText: string
  sectionType: string
  citations: Citation[]
  grounding: GroundingReport | null
  metrics: AgentMetrics | null
  warnings: string[]
  tokensResolved: ResolvedTokenInfo[]
  tokenWarnings: string[]
  contextWarnings: ContextWarning[]
  contextSectionsLoaded: LoadedContextSection[]
}) {
  const { t } = useTranslation('ai')

  return (
    <div className="space-y-4">
      <ContextLoadedPanel contextSectionsLoaded={contextSectionsLoaded} />
      <TokensResolvedPanel
        tokensResolved={tokensResolved}
        tokenWarnings={tokenWarnings}
        contextWarnings={contextWarnings}
      />
      {warnings.length > 0 && (
        <div className="rounded-md border border-review/40 bg-review-soft/40 p-3">
          <ul className="space-y-1 text-sm text-ink-muted">
            {warnings.map((code) => (
              <li key={code} className="flex items-start gap-2">
                <AlertTriangle
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 text-review"
                  aria-hidden="true"
                />
                <span>
                  {t(`warnings.${code}`, {
                    defaultValue: t('warnings.generic', { code }),
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Draft preview — render visual según section_type (tabla,
        lista, markdown). El editor TipTap lo reformatea al aceptar. */}
      <DraftPreview draftText={draftText} sectionType={sectionType} />

      {/* OT-0049 — observaciones de la verificación por oración, antes de aceptar. */}
      <GroundingFindingsSection report={grounding} />

      {/* Citations */}
      <CitationList citations={citations} />

      {/* Metrics — tokens visibles, NO costos USD */}
      {metrics ? <MetricsPanel metrics={metrics} /> : null}
    </div>
  )
}

function ApiErrorView({ code }: { code: string }) {
  const { t } = useTranslation('ai')
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft/30 p-3 text-sm text-ink">
      <p className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">
        {code}
      </p>
      <p>{t(`errors.${code}`, { defaultValue: code })}</p>
    </div>
  )
}

// ─── Helpers UI ──────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="min-w-6rem text-xs uppercase tracking-wider text-ink-subtle">
        {label}
      </span>
      <span className="text-ink">{value}</span>
    </div>
  )
}

function StepIcon({ status }: { status: StepState['status'] }) {
  if (status === 'complete') {
    return (
      <CheckCircle2
        className="mt-0.5 h-4 w-4 shrink-0 text-official"
        aria-hidden="true"
      />
    )
  }
  if (status === 'running') {
    return (
      <Loader2
        className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-brand"
        aria-hidden="true"
      />
    )
  }
  if (status === 'error') {
    return (
      <XCircle
        className="mt-0.5 h-4 w-4 shrink-0 text-critical"
        aria-hidden="true"
      />
    )
  }
  return (
    <Circle
      className="mt-0.5 h-4 w-4 shrink-0 text-ink-subtle/50"
      aria-hidden="true"
    />
  )
}

function MetricsPanel({ metrics }: { metrics: AgentMetrics }) {
  const { t } = useTranslation('ai')
  return (
    <div className="rounded-md border border-line bg-surface-muted/40 px-4 py-3 text-xs">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1.5">
        <Metric
          label={t('draft_meta.tokens_label')}
          value={t('draft_meta.tokens_format', {
            total: formatTokens(metrics.total_tokens),
            input: formatTokens(metrics.total_input_tokens),
            output: formatTokens(metrics.total_output_tokens),
          })}
        />
        {metrics.total_cached_input_tokens > 0 && (
          <Metric
            label={t('draft_meta.cached_tokens', {
              value: formatTokens(metrics.total_cached_input_tokens),
            })}
          />
        )}
        <Metric
          label={t('draft_meta.latency_label')}
          value={formatDuration(metrics.total_latency_ms, t)}
        />
        <Metric
          label={t('draft_meta.calls_label')}
          value={t('draft_meta.calls_format', {
            count: metrics.steps.filter((s) => s.role !== null).length,
          })}
        />
        <Metric
          label={t('draft_meta.revise_iterations_label')}
          value={String(metrics.revise_iterations)}
        />
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value?: string }) {
  if (value === undefined) {
    return <span className="text-ink-subtle">{label}</span>
  }
  return (
    <span>
      <span className="text-ink-subtle">{label}: </span>
      <span className="font-mono text-ink">{value}</span>
    </span>
  )
}

/** Formato compacto: 1234 → "1,234"; 12500 → "12.5K".
 *
 * Tokens son cifras TÉCNICAS de consumo IA — usamos `en-US` fijo igual
 * que `formatInt` en `usageHelpers.ts`. Razón documentada ahí: un user
 * mexicano que ve `1.234` (formato es-ES con punto miles) lo lee como
 * "uno con doscientos treinta y cuatro", confusión inaceptable para
 * cifras técnicas. */
function formatTokens(n: number): string {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1)}K`
  }
  return new Intl.NumberFormat('en-US').format(n)
}

/** Detecta tokens `{{section_id.value}}` en el texto del usuario. Devuelve
 * `valid` (raw strings cuyos section_ids existen) y `invalid` (no existen
 * en `validSectionIds`). El backend hace la sustitución real; esta
 * detección es solo UX inline para advertir antes de enviar.
 *
 * Por ahora solo soportamos `field === 'value'`. Si llega `{{x.tags}}` lo
 * marcamos como inválido para reflejar el warning del backend.
 */
function detectTokens(
  text: string,
  validSectionIds: Set<string>,
): { valid: string[]; invalid: string[] } {
  const valid: string[] = []
  const invalid: string[] = []
  const seen = new Set<string>()
  // Reset lastIndex porque TOKEN_REGEX es global.
  const re = new RegExp(TOKEN_REGEX.source, 'g')
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    const raw = m[0]
    if (raw === undefined) continue
    if (seen.has(raw)) continue
    seen.add(raw)
    const sectionId = m[1]
    const fieldName = m[2]
    if (
      sectionId !== undefined &&
      fieldName === 'value' &&
      validSectionIds.has(sectionId)
    ) {
      valid.push(raw)
    } else {
      invalid.push(raw)
    }
  }
  return { valid, invalid }
}

/** True si el `value` actual de la sección tiene contenido útil que
 * vale la pena pasar a la IA como referencia. Distingue:
 *   · string vacío / whitespace → NO
 *   · array vacío → NO
 *   · objeto sin keys o con todas las keys vacías → NO
 *   · TipTap doc con un solo párrafo vacío → NO
 *   · cualquier otra cosa → SÍ
 */
function isContentNonEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    // TipTap doc heurística — un párrafo vacío es "vacío"
    if (
      obj['type'] === 'doc' &&
      Array.isArray(obj['content']) &&
      obj['content'].length === 1 &&
      (obj['content'][0] as { type?: string; content?: unknown[] })?.type ===
        'paragraph' &&
      !(obj['content'][0] as { content?: unknown[] })?.content
    ) {
      return false
    }
    // structured_table { rows: [] } heurística
    if (
      Array.isArray(obj['rows']) &&
      Object.keys(obj).length === 1 &&
      obj['rows'].length === 0
    ) {
      return false
    }
    return Object.keys(obj).length > 0
  }
  return false
}

